import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DASHBOARD_CONFERENCE_HISTORY,
  DASHBOARD_STATCREW,
  getJobApproveUrl,
  getJobReviewPgsUrl,
  getJobReviewRosterMapBatchUrl,
  getJobReviewTgsUrl,
  getJobReviewUrl,
} from '../constants/routes';
import AuthService from '../services/authservices';
import { ConferenceLookupResponse } from '../services/conferenceLookupService';
import { formatMatchTypeForDisplay } from '../utils/statcrewArtifactPreview';
import MatchTypeSelector from './MatchTypeSelector';
import Spinner from './Spinner';
import ReviewWideStatsTable, { WideTablePayload } from './ReviewWideStatsTable';

const authService = new AuthService();
const PAGE_SIZE = 25;

type WarningItem = {
  code?: string;
  message?: string;
  filename?: string | null;
  detail?: Record<string, unknown>;
};

type HumanVerificationCandidate = {
  source_name: string;
  matched_name: string;
  proposed_player_id?: string | null;
  score?: number;
  status?: string;
  context?: string;
  season?: number | null;
  source_season?: number | null;
  source_position?: string | null;
  source_class?: string | null;
  source_uniform?: string | null;
  matched_position?: string | null;
  matched_class?: string | null;
  matched_uniform?: string | null;
  matched_season?: number | null;
  team_name?: string | null;
  is_new_player?: boolean;
};

type HumanVerificationSummary = {
  pending_count?: number;
  candidates?: HumanVerificationCandidate[];
};

type ReviewPayload = {
  job_id?: string;
  sport_code?: string;
  season?: number;
  team_code?: unknown;
  roster?: { players?: Record<string, unknown>[] };
  schedule?: { games?: Record<string, unknown>[] };
  flat_match?: Record<string, unknown>[];
  ambiguous_players?: Record<string, unknown>[];
  unresolved_pgs_count?: number;
  init_warnings?: WarningItem[];
  schedule_soft_filter?: { warnings?: WarningItem[]; filenames_removed?: string[] };
  physical_tables?: Record<string, string>;
  review_meta?: Record<string, unknown>;
  season_conference?: ConferenceLookupResponse | null;
  human_verification?: HumanVerificationSummary;
};

function Stepper({ currentStep, steps }: { currentStep: number; steps: string[] }) {
  return (
    <div className="mb-8 flex flex-wrap items-center gap-2">
      {steps.map((label, index) => {
        const n = index + 1;
        const done = n < currentStep;
        const active = n === currentStep;
        return (
          <React.Fragment key={label}>
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                active ? 'bg-cyan-100 text-cyan-800' : done ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/80 text-xs font-bold">
                {done ? '✓' : n}
              </span>
              {label}
            </div>
            {index < steps.length - 1 && <div className="hidden h-px w-6 bg-slate-200 sm:block" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Human-readable explanations for known approve-failure codes. The backend
// detail is e.g. "json_temp_row_mismatch: 51" — match on the code prefix.
const APPROVE_FAILURE_HINTS: [string, string][] = [
  ['json_temp_row_mismatch', 'Parsed game data and the staged temp rows disagree (often a job that was processed twice). Re-run processing for this job, then approve again.'],
  ['flat_match_missing_tgs', 'A schedule (flat match) row has no matching team-game stats. Usually a match-identity mismatch — re-run processing for this job.'],
  ['unresolved_player_ids', 'Some player rows have no player_id. Resolve identities in the Roster step, or approve with the unresolved-players option.'],
  ['pending_human_verification', 'Player identities are awaiting manual verification. Resolve them in the Roster step first.'],
  ['season_roster_not_in_pgs', 'Season roster players are missing from game stats. Review the roster before approving.'],
  ['no_temp_pgs_rows', 'No staged player stats found for this job — processing may not have completed.'],
  ['integration_already_queued', 'This job was already approved; integration is queued or running. Check the dashboard.'],
  ['job_not_ready', 'The job is not in a reviewable state, so it cannot be approved right now.'],
  ['conference', 'Team conference is not set for this season. Set it in the review page, then approve.'],
];

function approveFailureHint(detail: string): string | null {
  const d = detail.toLowerCase();
  const hit = APPROVE_FAILURE_HINTS.find(([code]) => d.includes(code));
  return hit ? hit[1] : null;
}

function isBlankOptional(value: unknown): boolean {
  const s = String(value ?? '').trim();
  return !s || s === '-' || s.toUpperCase() === 'N/A';
}

function previewPlayerName(first: string, family: string): string {
  const f = first.trim();
  const fa = family.trim();
  if (fa && f) return `${fa.toUpperCase()}, ${f.toUpperCase()}`;
  return (fa || f).toUpperCase();
}

function normalizeNameKey(name: unknown): string {
  return String(name ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function normalizeReviewPlayer(raw: Record<string, unknown>): Record<string, unknown> {
  const position = String(raw.position ?? raw.Position ?? '').trim();
  const playerClass = String(raw.class ?? raw.Class ?? '').trim();
  let first = String(raw.first_name ?? raw['First Name'] ?? '').trim();
  let family = String(raw.family_name ?? raw['Family Name'] ?? '').trim();
  const canonical = String(raw.canonical_name ?? raw.canonicalName ?? '').trim();
  if (!first && !family && canonical) {
    if (canonical.includes(',')) {
      const [famPart, firstPart] = canonical.split(',', 2).map((s) => s.trim());
      family = family || famPart;
      first = first || firstPart;
    } else {
      family = family || canonical;
    }
  }
  const playerName = String(
    raw.player_name ?? (previewPlayerName(first, family) || canonical),
  ).trim();
  return {
    ...raw,
    first_name: first,
    family_name: family,
    player_name: playerName,
    position,
    class: playerClass,
    registry_id: raw.registry_id ?? raw.registryId,
    player_id: raw.player_id ?? raw.playerId,
  };
}

function enrichGamesWithFlatMatch(
  games: Record<string, unknown>[],
  flatMatch: Record<string, unknown>[] | undefined,
): Record<string, unknown>[] {
  if (!flatMatch?.length) return games;
  return games.map((g) => {
    const date = String(g.date || '').trim();
    const opp = String(g.opponent || '').trim();
    const fm = flatMatch.find((row) => {
      const rowDate = String(row.game_date ?? row.date ?? '').trim();
      const rowOpp = String(row.opponent ?? '').trim();
      return rowDate === date && rowOpp === opp;
    });
    if (!fm) return g;
    const matchType = g.match_type ?? g.matchType ?? fm.match_type ?? fm.matchType;
    return {
      ...g,
      match_type: matchType,
      flat_match_id: fm.id,
      sc_match_id: fm.sc_match_id ?? fm.scMatchId,
    };
  });
}

function candidatesForPlayer(
  player: Record<string, unknown>,
  candidates: HumanVerificationCandidate[],
): HumanVerificationCandidate[] {
  const key = normalizeNameKey(player.player_name);
  if (!key) return [];
  const seen = new Set<string>();
  return candidates.filter((c) => {
    if (normalizeNameKey(c.source_name) !== key) return false;
    // New players have no proposed_player_id; skip showing the dropdown for them.
    if (!c.proposed_player_id) return false;
    const pid = String(c.proposed_player_id);
    if (seen.has(pid)) return false;
    seen.add(pid);
    return true;
  });
}

type IdentityReviewPanelProps = {
  player: Record<string, unknown>;
  candidates: HumanVerificationCandidate[];
  selectedCandidate?: HumanVerificationCandidate;
  applyingMappings: boolean;
  onSelect: (candidate: HumanVerificationCandidate) => void;
  onSelectNone: () => void;
  onClear: () => void;
  onClose: () => void;
};

function IdentityReviewPanel({
  player,
  candidates,
  selectedCandidate,
  applyingMappings,
  onSelect,
  onSelectNone,
  onClear,
  onClose,
}: IdentityReviewPanelProps) {
  const noneSelected = !!selectedCandidate && selectedCandidate.is_new_player;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className="my-8 w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="identity-panel-title"
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 id="identity-panel-title" className="text-lg font-semibold text-slate-900">
              Review identity matches
            </h3>
            <p className="text-sm text-slate-600">
              Choose the existing player below that matches{' '}
              <span className="font-medium text-slate-900">{String(player.player_name || '')}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rostered player</p>
          <p className="font-medium text-slate-900">{String(player.player_name || '')}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 text-sm text-slate-600">
            {player.position ? <span>Position: {String(player.position)}</span> : null}
            {player.class ? <span>Class: {String(player.class)}</span> : null}
          </div>
        </div>

        {selectedCandidate && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            Selected:{' '}
            <span className="font-semibold">
              {selectedCandidate.is_new_player
                ? `New player — ${selectedCandidate.matched_name || 'unknown'}`
                : selectedCandidate.matched_name}
            </span>
            <button
              type="button"
              onClick={onClear}
              disabled={applyingMappings}
              className="ml-3 text-xs font-medium text-emerald-700 underline hover:text-emerald-800 disabled:opacity-50"
            >
              Clear selection
            </button>
          </div>
        )}

        <p className="mb-2 text-sm font-medium text-slate-700">Possible matches</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {candidates.map((c) => {
            const isSelected =
              !!selectedCandidate &&
              String(selectedCandidate.proposed_player_id) === String(c.proposed_player_id);
            return (
              <div
                key={String(c.proposed_player_id)}
                className={`flex flex-col rounded-xl border p-4 transition ${
                  isSelected
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-slate-200 bg-white hover:border-cyan-400 hover:bg-slate-50'
                }`}
              >
                <div className="mb-3">
                  <p className="font-semibold text-slate-900">{c.matched_name || 'Unknown'}</p>
                  <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                    {c.matched_position && <li>Position: {c.matched_position}</li>}
                    {c.matched_class && <li>Class: {c.matched_class}</li>}
                    {c.matched_uniform && <li>Jersey: #{c.matched_uniform}</li>}
                    {c.matched_season && <li>Season: {c.matched_season}</li>}
                  </ul>
                </div>
                <div className="mt-auto">
                  <button
                    type="button"
                    disabled={applyingMappings || isSelected}
                    onClick={() => onSelect(c)}
                    className={`w-full rounded-lg px-3 py-2 text-sm font-semibold ${
                      isSelected
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-cyan-600 text-white hover:bg-cyan-700'
                    } disabled:opacity-50`}
                  >
                    {isSelected ? 'Selected' : 'Select this identity'}
                  </button>
                </div>
              </div>
            );
          })}
          <div
            key="__none__"
            className={`flex flex-col rounded-xl border p-4 transition ${
              noneSelected
                ? 'border-emerald-400 bg-emerald-50'
                : 'border-slate-200 bg-white hover:border-cyan-400 hover:bg-slate-50'
            }`}
          >
            <div className="mb-3">
              <p className="font-semibold text-slate-900">None of these</p>
              <p className="text-sm text-slate-600">This is a new player.</p>
            </div>
            <div className="mt-auto">
              <button
                type="button"
                disabled={applyingMappings || noneSelected}
                onClick={onSelectNone}
                className={`w-full rounded-lg px-3 py-2 text-sm font-semibold ${
                  noneSelected
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-cyan-600 text-white hover:bg-cyan-700'
                } disabled:opacity-50`}
              >
                {noneSelected ? 'Selected' : 'Select — new player'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function WarningsPanel({ warnings }: { warnings: WarningItem[] }) {
  if (!warnings.length) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
      <p className="text-sm font-semibold text-amber-900">Warnings ({warnings.length})</p>
      <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-amber-900">
        {warnings.map((w, i) => (
          <li key={i}>
            {w.filename ? `[${w.filename}] ` : ''}
            {w.message || w.code || 'Warning'}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function StatCrewReviewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get('jobId') ?? searchParams.get('job_id') ?? '';
  const username = searchParams.get('username') ?? '';
  const selectedSport = searchParams.get('selectedSport') ?? '';

  const steps = ['Roster', 'Schedule', 'Temp PGS', 'Temp TGS', 'Approve'];
  const [currentStep, setCurrentStep] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<ReviewPayload | null>(null);
  const [players, setPlayers] = useState<Record<string, unknown>[]>([]);
  const [games, setGames] = useState<Record<string, unknown>[]>([]);
  const [baselineGames, setBaselineGames] = useState<Record<string, unknown>[]>([]);
  const [pgsPayload, setPgsPayload] = useState<WideTablePayload | null>(null);
  const [tgsPayload, setTgsPayload] = useState<WideTablePayload | null>(null);
  const [pgsOffset, setPgsOffset] = useState(0);
  const [tgsOffset, setTgsOffset] = useState(0);
  const [pgsLoading, setPgsLoading] = useState(false);
  const [tgsLoading, setTgsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  // Ref guard against same-tick double clicks on Approve (state updates are
  // async, so two rapid clicks can both see approving === false).
  const approveInFlightRef = useRef(false);
  // Approve failure shown as a red pop-up alert (not the muted saveMessage
  // line, which is easy to miss in the whitespace under the stepper).
  const [approveAlert, setApproveAlert] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  // Rejected roster edits (e.g. invalid player_class). Rendered as a red
  // pop-up alert so the operator can't miss them — distinct from the muted
  // saveMessage line.
  const [rejectionErrors, setRejectionErrors] = useState<
    { index: number; message: string }[] | null
  >(null);
  const [pendingMappings, setPendingMappings] = useState<
    Record<number, HumanVerificationCandidate>
  >({});
  const [applyingMappings, setApplyingMappings] = useState(false);
  const [editingGameIndex, setEditingGameIndex] = useState<number | null>(null);
  const [draftMatchType, setDraftMatchType] = useState('');
  const [savingMatchTypeIndex, setSavingMatchTypeIndex] = useState<number | null>(null);
  const [identityPanelIndex, setIdentityPanelIndex] = useState<number | null>(null);

  const seasonConference = review?.season_conference ?? null;

  const navigateToConferenceHistory = useCallback(() => {
    const params = new URLSearchParams();
    if (username) params.set('username', username);
    if (selectedSport) params.set('selectedSport', selectedSport);
    navigate(`${DASHBOARD_CONFERENCE_HISTORY}?${params.toString()}`);
  }, [navigate, selectedSport, username]);

  const allWarnings = useMemo(() => {
    const init = review?.init_warnings || [];
    const soft = review?.schedule_soft_filter?.warnings || [];
    return [...init, ...soft];
  }, [review]);

  // `background: true` refreshes review data without taking over the full-page
  // spinner or the error screen — used after a step has already advanced, so a
  // slow/failed refresh can't pin the UI or blow away the current step.
  const loadReview = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background ?? false;
    if (!jobId) {
      setError('Missing job ID');
      setLoading(false);
      return;
    }
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await authService.authenticatedFetch(getJobReviewUrl(jobId));
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || 'Failed to load review data');
      }
      const data = (await res.json()) as ReviewPayload;
      setReview(data);
      const normalized = (data.roster?.players || []).map((p) =>
        normalizeReviewPlayer(p as Record<string, unknown>),
      );
      setPlayers(normalized);
      const enriched = enrichGamesWithFlatMatch(
        [...(data.schedule?.games || [])],
        data.flat_match,
      );
      setGames(enriched);
      setBaselineGames(enriched.map((g) => ({ ...g })));
      setEditingGameIndex(null);
      setDraftMatchType('');
      setSavingMatchTypeIndex(null);
    } catch (e) {
      if (background) {
        console.warn('Background review refresh failed:', e);
      } else {
        setError(e instanceof Error ? e.message : 'Load failed');
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [jobId]);

  const loadPgs = useCallback(async (offset: number) => {
    if (!jobId) return;
    setPgsLoading(true);
    try {
      const res = await authService.authenticatedFetch(
        `${getJobReviewPgsUrl(jobId)}?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      if (res.ok) setPgsPayload(await res.json());
    } finally {
      setPgsLoading(false);
    }
  }, [jobId]);

  const loadTgs = useCallback(async (offset: number) => {
    if (!jobId) return;
    setTgsLoading(true);
    try {
      const res = await authService.authenticatedFetch(
        `${getJobReviewTgsUrl(jobId)}?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      if (res.ok) setTgsPayload(await res.json());
    } finally {
      setTgsLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    void loadReview();
  }, [loadReview]);

  useEffect(() => {
    if (currentStep === 3) void loadPgs(pgsOffset);
  }, [currentStep, pgsOffset, loadPgs]);

  useEffect(() => {
    if (currentStep === 4) void loadTgs(tgsOffset);
  }, [currentStep, tgsOffset, loadTgs]);

  useEffect(() => {
    if (identityPanelIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIdentityPanelIndex(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [identityPanelIndex]);

  const saveRoster = async (): Promise<boolean> => {
    if (!jobId) return false;
    setSaving(true);
    setSaveMessage(null);
    setRejectionErrors(null);
    try {
      const res = await authService.authenticatedFetch(`${getJobReviewUrl(jobId)}/roster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          players: players.map((p) => ({
            player_id: p.player_id,
            registry_id: p.registry_id,
            first_name: p.first_name,
            family_name: p.family_name,
            position: p.position,
            class: p.class,
          })),
        }),
      });
      const result = (await res.json().catch(() => ({}))) as {
        detail?: string;
        updated_count?: number;
        rejected?: { index: number; message: string }[];
        unresolved_pgs_count?: number;
      };
      if (!res.ok) throw new Error(result.detail || 'Roster save failed');
      const rejected = result.rejected ?? [];
      if (rejected.length) {
        // Show the rejections in a red pop-up alert (not just the muted text
        // line) so the operator clearly sees which edits need correcting.
        setRejectionErrors(
          rejected.map((r) => ({ index: r.index, message: r.message })),
        );
        setSaveMessage(null);
        return false;
      }
      setSaveMessage(
        `Saved ${result.updated_count ?? 0} player(s). Unresolved PGS: ${result.unresolved_pgs_count ?? '?'}`,
      );
      // Success is determined solely by the save response. The review refresh is
      // no longer chained here (it used to gate this return on a second, full-page
      // reload that could stall), so advancing never waits on it.
      return true;
    } catch (e) {
      setSaveMessage(e instanceof Error ? e.message : 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNextFromRoster = async () => {
    const saved = await saveRoster();
    if (!saved) return;
    const mappingsApplied = await applyPendingMappings();
    if (!mappingsApplied) return;
    // Advance immediately on a successful save, then refresh review data in the
    // background so a slow/failed reload can't pin the "Loading…" spinner or
    // strand the user on the roster step.
    setCurrentStep(2);
    void loadReview({ background: true });
  };

  const saveSchedule = async (): Promise<boolean> => {
    if (!jobId) return false;
    setSaving(true);
    setSaveMessage(null);
    try {
      const edits = games
        .map((g, i) => {
          const before = baselineGames[i];
          if (!before) return null;
          const beforeOpp = String(before.opponent || '');
          const afterOpp = String(g.opponent || '');
          const beforeMt = String(before.match_type || before.matchType || '');
          const afterMt = String(g.match_type || g.matchType || '');
          const beforeConf = String(before.opponent_conference_name || '');
          const afterConf = String(g.opponent_conference_name || '');
          const beforeConfTidy = String(before.opponent_conference_tidy_name || '');
          const afterConfTidy = String(g.opponent_conference_tidy_name || '');
          if (
            beforeOpp === afterOpp &&
            beforeMt === afterMt &&
            beforeConf === afterConf &&
            beforeConfTidy === afterConfTidy
          ) {
            return null;
          }
          return {
            before: {
              opponent: beforeOpp,
              date: before.date,
              match_type: beforeMt,
              opponent_conference_name: beforeConf,
              opponent_conference_tidy_name: beforeConfTidy,
            },
            after: {
              opponent: afterOpp,
              date: g.date,
              match_type: afterMt,
              opponent_conference_name: afterConf,
              opponent_conference_tidy_name: afterConfTidy,
            },
          };
        })
        .filter(Boolean);
      if (edits.length === 0) {
        setSaveMessage('No schedule changes to save.');
        return true;
      }
      const res = await authService.authenticatedFetch(`${getJobReviewUrl(jobId)}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edits }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((result as { detail?: string }).detail || 'Schedule save failed');
      setSaveMessage('Schedule updated.');
      // Background refresh: re-syncs games/baselines (so the same edit isn't
      // re-sent on the next save) without flashing the full-page spinner.
      // Awaited so baselines are consistent before the user can save again
      // (`saving` stays true until this settles).
      await loadReview({ background: true });
      return true;
    } catch (e) {
      setSaveMessage(e instanceof Error ? e.message : 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveRowEdits = async (index: number) => {
    if (!jobId) return;
    const g = games[index];
    const before = baselineGames[index];
    if (!g || !before) return;

    const afterMt = draftMatchType;
    const beforeMt = String(before.match_type || before.matchType || '');
    const beforeOpp = String(before.opponent || '');
    const afterOpp = String(g.opponent || '');
    const beforeConf = String(before.opponent_conference_name || '');
    const afterConf = String(g.opponent_conference_name || '');
    const beforeConfTidy = String(before.opponent_conference_tidy_name || '');
    const afterConfTidy = String(g.opponent_conference_tidy_name || '');

    setGames((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], match_type: afterMt };
      return next;
    });

    if (
      beforeOpp === afterOpp &&
      beforeMt === afterMt &&
      beforeConf === afterConf &&
      beforeConfTidy === afterConfTidy
    ) {
      setEditingGameIndex(null);
      setDraftMatchType('');
      return;
    }

    setSavingMatchTypeIndex(index);
    setSaveMessage(null);
    try {
      const res = await authService.authenticatedFetch(`${getJobReviewUrl(jobId)}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          edits: [
            {
              before: {
                opponent: beforeOpp,
                date: before.date,
                match_type: beforeMt,
                opponent_conference_name: beforeConf,
                opponent_conference_tidy_name: beforeConfTidy,
              },
              after: {
                opponent: afterOpp,
                date: g.date,
                match_type: afterMt,
                opponent_conference_name: afterConf,
                opponent_conference_tidy_name: afterConfTidy,
              },
            },
          ],
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((result as { detail?: string }).detail || 'Row save failed');
      }
      setEditingGameIndex(null);
      setDraftMatchType('');
      setSaveMessage('Row updated.');
      // Background refresh — same rationale as saveSchedule: sync baselines
      // without the full-page "Loading review data…" takeover.
      await loadReview({ background: true });
    } catch (e) {
      setSaveMessage(e instanceof Error ? e.message : 'Row save failed');
    } finally {
      setSavingMatchTypeIndex(null);
    }
  };

  const cancelRowEdits = () => {
    if (editingGameIndex == null) return;
    setGames((prev) => {
      const next = [...prev];
      next[editingGameIndex] = { ...baselineGames[editingGameIndex] };
      return next;
    });
    setEditingGameIndex(null);
    setDraftMatchType('');
  };

  const selectIdentity = (index: number, candidate: HumanVerificationCandidate) => {
    if (!candidate.proposed_player_id) return;
    setPendingMappings((prev) => ({ ...prev, [index]: candidate }));
  };

  const clearIdentitySelection = (index: number) => {
    setPendingMappings((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const selectNone = (index: number) => {
    const player = players[index];
    const playerId = String(player?.player_id || '');
    if (!playerId) return;
    setPendingMappings((prev) => ({
      ...prev,
      [index]: {
        source_name: String(player?.player_name || ''),
        matched_name: String(player?.player_name || ''),
        proposed_player_id: playerId,
        is_new_player: true,
      },
    }));
  };

  const applyPendingMappings = async (): Promise<boolean> => {
    if (!jobId) return false;
    const mappings = Object.entries(pendingMappings).map(([index, candidate]) => {
      const player = players[Number(index)];
      const sourceName = String(
        player?.player_name || candidate.source_name || '',
      );
      return {
        source_name: sourceName,
        new_player_id: candidate.proposed_player_id,
        matched_name: candidate.matched_name,
      };
    });
    if (mappings.length === 0) return true;

    setApplyingMappings(true);
    setSaveMessage(null);
    try {
      const res = await authService.authenticatedFetch(getJobReviewRosterMapBatchUrl(jobId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings }),
      });
      const result = (await res.json().catch(() => ({}))) as {
        detail?: string;
        success?: boolean;
        updated_count?: number;
        pending_human_verification?: number;
      };
      if (!res.ok) throw new Error(result.detail || 'Identity mapping failed');
      setSaveMessage(
        `Mapped ${result.updated_count ?? 0} identity(ies). Pending verification: ${result.pending_human_verification ?? '?'}.`,
      );
      setPendingMappings({});
      // Refresh in the background so applying mappings never gates the roster
      // step advance on a full-page reload (handleNextFromRoster advances right
      // after this resolves).
      await loadReview({ background: true });
      return true;
    } catch (e) {
      setSaveMessage(e instanceof Error ? e.message : 'Identity mapping failed');
      return false;
    } finally {
      setApplyingMappings(false);
    }
  };

  const renderIdentityCell = (p: Record<string, unknown>, i: number) => {
    const options = candidatesForPlayer(p, review?.human_verification?.candidates || []);
    if (!options.length) return null;
    const pending = pendingMappings[i];
    const resolved =
      pending ??
      options.find((o) => String(o.proposed_player_id) === String(p.player_id || ''));
    return (
      <button
        type="button"
        disabled={applyingMappings}
        onClick={() => setIdentityPanelIndex(i)}
        className="inline-flex flex-wrap items-center gap-2 text-left disabled:opacity-50"
      >
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-200">
          {options.length} possible match{options.length === 1 ? '' : 'es'} — click to review
        </span>
        {resolved && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            <span>✓</span>
            {resolved.is_new_player ? 'New player' : resolved.matched_name}
          </span>
        )}
      </button>
    );
  };

  const handleNextFromSchedule = async () => {
    const scheduleOk = await saveSchedule();
    if (!scheduleOk) return;
    setCurrentStep(3);
  };

  const approve = async () => {
    if (!jobId || approveInFlightRef.current) return;
    const pendingCount = review?.human_verification?.pending_count ?? 0;
    const unresolvedCount = review?.unresolved_pgs_count ?? 0;

    if (pendingCount > 0 || unresolvedCount > 0) {
      const parts: string[] = [];
      if (pendingCount > 0) {
        parts.push(`${pendingCount} player identity(ies) not verified`);
      }
      if (unresolvedCount > 0) {
        parts.push(`${unresolvedCount} PGS row(s) missing player_id`);
      }
      const confirmed = window.confirm(`${parts.join('; ')}. Approve anyway?`);
      if (!confirmed) {
        setSaveMessage('Approval cancelled.');
        return;
      }
    }

    approveInFlightRef.current = true;
    setApproving(true);
    setSaveMessage(null);
    setApproveAlert(null);
    try {
      const res = await authService.authenticatedFetch(getJobApproveUrl(jobId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allow_unresolved_player_ids: unresolvedCount > 0,
          allow_pending_human_verification: pendingCount > 0,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((result as { detail?: string }).detail || 'Approve failed');
      const queued = (result as { status?: string }).status === 'queued';
      setSaveMessage(
        queued
          ? 'Integration queued on worker. You can leave this page — check StatCrew dashboard for completion.'
          : 'Approved.',
      );
      // Success: deliberately KEEP the button disabled (no setApproving(false))
      // so it can't be clicked again during the redirect delay window.
      if (queued) {
        setTimeout(() => navigate(DASHBOARD_STATCREW), 2500);
      } else {
        navigate(DASHBOARD_STATCREW);
      }
    } catch (e) {
      // Surface the halt as a red pop-up alert instead of the muted text line.
      setApproveAlert(e instanceof Error ? e.message : 'Approve failed');
      // Failure: re-enable so the operator can fix the issue and retry.
      approveInFlightRef.current = false;
      setApproving(false);
    }
  };

  const nullPositionClassCount = players.filter(
    (p) => isBlankOptional(p.position) || isBlankOptional(p.class),
  ).length;

  if (loading) {
    return <div className="p-8 text-slate-600">Loading review data…</div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <p className="text-red-600">{error}</p>
        <button type="button" className="mt-4 text-sm underline" onClick={() => void loadReview()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Review upload</h1>
          <p className="text-sm text-slate-500">
            Job {jobId}
            {review?.sport_code ? ` · ${review.sport_code}` : ''}
            {review?.season ? ` · ${review.season}` : ''}
            {review?.team_code != null ? ` · team ${String(review.team_code)}` : ''}
          </p>
        </div>
        <button type="button" onClick={() => navigate(-1)} className="text-sm text-slate-600 underline">
          Back
        </button>
      </div>

      <Stepper currentStep={currentStep} steps={steps} />
      {saveMessage && <p className="text-sm text-slate-600">{saveMessage}</p>}

      {/* Red pop-up alert when Approve & integrate is halted (data-quality
          gate, not-ready state, …). Same treatment as roster rejections —
          the muted saveMessage line in the whitespace was easy to miss. */}
      {approveAlert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="approve-alert-title"
        >
          <div className="w-full max-w-lg rounded-2xl border-2 border-red-500 bg-white shadow-2xl">
            <div className="flex items-center gap-3 rounded-t-xl bg-red-600 px-5 py-3">
              <span className="text-xl text-white">⚠</span>
              <h3 id="approve-alert-title" className="text-base font-semibold text-white">
                Approval halted
              </h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm font-semibold text-red-700">{approveAlert}</p>
              {approveFailureHint(approveAlert) && (
                <p className="text-sm text-slate-700">{approveFailureHint(approveAlert)}</p>
              )}
              <p className="text-sm text-slate-600">
                Nothing was queued — the job is unchanged and can be approved
                again once the issue is resolved.
              </p>
            </div>
            <div className="flex justify-end rounded-b-2xl border-t border-slate-200 bg-slate-50 px-5 py-3">
              <button
                type="button"
                onClick={() => setApproveAlert(null)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Red pop-up alert for rejected roster edits (e.g. invalid player class).
          Deliberately loud — the muted saveMessage line was easy to miss. */}
      {rejectionErrors && rejectionErrors.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="rejection-alert-title"
        >
          <div className="w-full max-w-lg rounded-2xl border-2 border-red-500 bg-white shadow-2xl">
            <div className="flex items-center gap-3 rounded-t-xl bg-red-600 px-5 py-3">
              <span className="text-xl text-white">⚠</span>
              <h3 id="rejection-alert-title" className="text-base font-semibold text-white">
                Some edits were rejected
              </h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <ul className="list-disc space-y-2 pl-5 text-sm font-medium text-red-700">
                {rejectionErrors.map((r, i) => (
                  <li key={i}>
                    <span className="font-semibold">Row {r.index + 1}:</span> {r.message}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-slate-600">
                Please correct the highlighted value(s) and save again. Nothing was
                written for the rejected row(s).
              </p>
            </div>
            <div className="flex justify-end rounded-b-2xl border-t border-slate-200 bg-slate-50 px-5 py-3">
              <button
                type="button"
                onClick={() => setRejectionErrors(null)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Fix my edits
              </button>
            </div>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Roster</h2>
              <p className="text-sm text-slate-500">
                Edits and identity selections are saved when you continue. Names are fuzzy-checked.
                Position/Class are editable.
                {nullPositionClassCount > 0 && (
                  <span className="ml-1 font-medium text-amber-700">
                    {nullPositionClassCount} player(s) need Position or Class.
                  </span>
                )}
                {Object.keys(pendingMappings).length > 0 && (
                  <span className="ml-1 font-medium text-cyan-700">
                    {Object.keys(pendingMappings).length} identity selection(s) pending.
                  </span>
                )}
              </p>
            </div>
          </div>
          {(review?.ambiguous_players?.length ?? 0) > 0 && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              {review?.ambiguous_players?.length} ambiguous identity resolution(s) during upload.
            </div>
          )}
          {(review?.human_verification?.pending_count ?? 0) > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {review?.human_verification?.pending_count} player(s) require human verification. Click the
              identity badge in each row to review possible matches.
            </div>
          )}
          <div className="max-h-[480px] overflow-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">First</th>
                  <th className="px-3 py-2 text-left">Family</th>
                  <th className="px-3 py-2 text-left">Player name</th>
                  <th className="px-3 py-2 text-left">Position</th>
                  <th className="px-3 py-2 text-left">Class</th>
                  <th className="px-3 py-2 text-left">Identity</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => {
                  return (
                    <tr key={String(p.registry_id || i)} className="border-t border-slate-100">
                      <td className="px-3 py-1">
                        <input
                          className="w-full rounded border px-2 py-1"
                          value={String(p.first_name || '')}
                          onChange={(e) => {
                            const first = e.target.value;
                            const next = [...players];
                            next[i] = {
                              ...p,
                              first_name: first,
                              player_name: previewPlayerName(first, String(p.family_name || '')),
                            };
                            setPlayers(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          className="w-full rounded border px-2 py-1"
                          value={String(p.family_name || '')}
                          onChange={(e) => {
                            const family = e.target.value;
                            const next = [...players];
                            next[i] = {
                              ...p,
                              family_name: family,
                              player_name: previewPlayerName(String(p.first_name || ''), family),
                            };
                            setPlayers(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-1 text-slate-500">{String(p.player_name || '')}</td>
                      <td className="px-3 py-1">
                        <input
                          className="w-full rounded border px-2 py-1"
                          value={String(p.position || '')}
                          onChange={(e) => {
                            const next = [...players];
                            next[i] = { ...p, position: e.target.value };
                            setPlayers(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          className="w-full rounded border px-2 py-1"
                          value={String(p.class || '')}
                          onChange={(e) => {
                            const next = [...players];
                            next[i] = { ...p, class: e.target.value };
                            setPlayers(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-1">
                        {renderIdentityCell(p, i)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleNextFromRoster()}
              disabled={saving || applyingMappings}
              className="rounded-xl bg-cyan-600 px-5 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {applyingMappings
                ? 'Applying identities…'
                : saving
                  ? 'Saving…'
                  : 'Next: Schedule →'}
            </button>
          </div>
        </section>
      )}

      {currentStep === 2 && (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Schedule</h2>
              <p className="text-sm text-slate-500">
                Edit opponents and match types. Conference is read from team conference lookup for
                this season — update it in Conference History if needed.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void saveSchedule()}
              disabled={saving}
              className="text-sm text-cyan-700 underline disabled:opacity-50"
            >
              Save schedule edits
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            <p className="font-medium text-slate-800">
              Conference for season {review?.season ?? '—'}
            </p>
            {seasonConference?.found && seasonConference.displayLabel ? (
              <p className="mt-1 text-slate-700">{seasonConference.displayLabel}</p>
            ) : (
              <p className="mt-1 text-amber-800">
                No conference affiliation found for this team and season.
              </p>
            )}
            <button
              type="button"
              onClick={navigateToConferenceHistory}
              className="mt-2 text-xs font-semibold text-cyan-600 hover:text-cyan-700"
            >
              Edit in Conference History →
            </button>
          </div>

          <WarningsPanel warnings={allWarnings} />

          <div className="max-h-[480px] overflow-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Opponent</th>
                  <th className="px-3 py-2 text-left">Score</th>
                  <th className="px-3 py-2 text-left">Opponent Conference</th>
                  <th className="px-3 py-2 text-left">Conf. Short</th>
                  <th className="px-3 py-2 text-left">Match type</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {games.map((g, i) => {
                  const isEditing = editingGameIndex === i;
                  const isSavingMatchType = savingMatchTypeIndex === i;
                  const anotherRowEditing = editingGameIndex !== null && editingGameIndex !== i;
                  const matchTypeLabel = formatMatchTypeForDisplay(
                    String(g.match_type || g.matchType || ''),
                  );

                  return (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-1">{String(g.date || '')}</td>
                    <td className="px-3 py-1">
                      <input
                        className="w-full rounded border px-2 py-1 disabled:border-transparent disabled:bg-slate-50 disabled:text-slate-700"
                        disabled={!isEditing}
                        value={String(g.opponent || '')}
                        onChange={(e) => {
                          const next = [...games];
                          next[i] = { ...g, opponent: e.target.value };
                          setGames(next);
                        }}
                      />
                    </td>
                    <td className="px-3 py-1">{String(g.score || '')}</td>
                    <td className="px-3 py-1">
                      <input
                        className="w-full rounded border px-2 py-1 disabled:border-transparent disabled:bg-slate-50 disabled:text-slate-700"
                        disabled={!isEditing}
                        value={String(g.opponent_conference_name || '')}
                        onChange={(e) => {
                          const next = [...games];
                          next[i] = { ...g, opponent_conference_name: e.target.value };
                          setGames(next);
                        }}
                      />
                    </td>
                    <td className="px-3 py-1">
                      <input
                        className="w-full rounded border px-2 py-1 disabled:border-transparent disabled:bg-slate-50 disabled:text-slate-700"
                        disabled={!isEditing}
                        value={String(g.opponent_conference_tidy_name || '')}
                        onChange={(e) => {
                          const next = [...games];
                          next[i] = { ...g, opponent_conference_tidy_name: e.target.value };
                          setGames(next);
                        }}
                      />
                    </td>
                    <td className="px-3 py-1">
                      {isEditing ? (
                        <MatchTypeSelector
                          initialValue={draftMatchType}
                          disabled={isSavingMatchType}
                          onChange={setDraftMatchType}
                        />
                      ) : (
                        <span className="text-slate-700">{matchTypeLabel || '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isSavingMatchType}
                            onClick={() => void saveRowEdits(i)}
                            className="text-sm font-semibold text-sky-600 hover:text-sky-800 disabled:opacity-50"
                          >
                            {isSavingMatchType ? <Spinner size="sm" /> : 'Save'}
                          </button>
                          <button
                            type="button"
                            disabled={isSavingMatchType}
                            onClick={() => cancelRowEdits()}
                            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={anotherRowEditing}
                          onClick={() => {
                            setEditingGameIndex(i);
                            setDraftMatchType(String(g.match_type || g.matchType || ''));
                          }}
                          className="text-sm font-semibold text-sky-600 hover:text-sky-800 disabled:opacity-50"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <button type="button" onClick={() => setCurrentStep(1)} className="text-sm text-slate-600 underline">
              ← Back
            </button>
            <button
              type="button"
              onClick={() => void handleNextFromSchedule()}
              disabled={saving}
              className="rounded-xl bg-cyan-600 px-5 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Next: Temp PGS →'}
            </button>
          </div>
        </section>
      )}

      {currentStep === 3 && (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <ReviewWideStatsTable
            title="Temp PGS (read-only)"
            payload={pgsPayload}
            loading={pgsLoading}
            pageSize={PAGE_SIZE}
            offset={pgsOffset}
            onPageChange={setPgsOffset}
          />
          <div className="flex justify-between">
            <button type="button" onClick={() => setCurrentStep(2)} className="text-sm text-slate-600 underline">
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className="rounded-xl bg-cyan-600 px-5 py-2 text-sm font-semibold text-white hover:bg-cyan-700"
            >
              Next: Temp TGS →
            </button>
          </div>
        </section>
      )}

      {currentStep === 4 && (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <ReviewWideStatsTable
            title="Temp TGS (read-only)"
            payload={tgsPayload}
            loading={tgsLoading}
            pageSize={PAGE_SIZE}
            offset={tgsOffset}
            onPageChange={setTgsOffset}
          />
          <div className="flex justify-between">
            <button type="button" onClick={() => setCurrentStep(3)} className="text-sm text-slate-600 underline">
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(5)}
              className="rounded-xl bg-cyan-600 px-5 py-2 text-sm font-semibold text-white hover:bg-cyan-700"
            >
              Next: Approve →
            </button>
          </div>
        </section>
      )}

      {currentStep === 5 && (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Approve & integrate</h2>
          <p className="text-sm text-slate-600">
            Approving merges temp data into main tables for team{' '}
            <strong>{String(review?.team_code ?? '—')}</strong> season{' '}
            <strong>{String(review?.season ?? '—')}</strong>.
          </p>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <dt className="text-slate-500">Temp PGS table</dt>
              <dd className="font-mono">{review?.physical_tables?.temp_pgs || '—'}</dd>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <dt className="text-slate-500">Temp TGS table</dt>
              <dd className="font-mono">{review?.physical_tables?.temp_tgs || '—'}</dd>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <dt className="text-slate-500">Unresolved PGS rows</dt>
              <dd>{review?.unresolved_pgs_count ?? 0}</dd>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <dt className="text-slate-500">Roster players</dt>
              <dd>{players.length}</dd>
            </div>
          </dl>
          {(review?.human_verification?.pending_count ?? 0) > 0 && (
            <p className="text-sm font-medium text-amber-700">
              {review?.human_verification?.pending_count} player identity(ies) not verified — defaults
              will be used on approve.
            </p>
          )}
          {(review?.unresolved_pgs_count ?? 0) > 0 && (
            <p className="text-sm font-medium text-amber-700">
              {review?.unresolved_pgs_count} PGS row(s) still missing player_id — you will be asked to
              confirm before approving.
            </p>
          )}
          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => setCurrentStep(4)} className="text-sm text-slate-600 underline">
              ← Back
            </button>
            <button
              type="button"
              onClick={() => void approve()}
              disabled={approving}
              className="rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {approving ? 'Approving…' : 'Approve & integrate'}
            </button>
          </div>
        </section>
      )}

      {identityPanelIndex !== null && players[identityPanelIndex] && (
        <IdentityReviewPanel
          player={players[identityPanelIndex]}
          candidates={candidatesForPlayer(
            players[identityPanelIndex],
            review?.human_verification?.candidates || [],
          )}
          selectedCandidate={
            pendingMappings[identityPanelIndex] ??
            candidatesForPlayer(
              players[identityPanelIndex],
              review?.human_verification?.candidates || [],
            ).find(
              (o) =>
                String(o.proposed_player_id) ===
                String(players[identityPanelIndex].player_id || ''),
            )
          }
          applyingMappings={applyingMappings}
          onSelect={(candidate) => {
            selectIdentity(identityPanelIndex, candidate);
            setIdentityPanelIndex(null);
          }}
          onSelectNone={() => {
            selectNone(identityPanelIndex);
            setIdentityPanelIndex(null);
          }}
          onClear={() => {
            clearIdentitySelection(identityPanelIndex);
          }}
          onClose={() => setIdentityPanelIndex(null)}
        />
      )}
    </div>
  );
}
